import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Box, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material'
import { AccountTree } from '@mui/icons-material'
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { type RequestArticleData, type ArticleWithQuantity, type BOMArticleGroup } from '../../../utils/types'
import { type Article } from '../../../../Item/utils/types'
import { fetchBOMLeafArticles, type BOMLeafArticle } from '../../../../Bom/api/bomApi'
import LogTool from '../../../../../logger/logTools'

type Props = {
  requestArticles?: RequestArticleData[]
}

/**
 * Component to render expanded row content showing articles with their quantities.
 * Used in the Request overview table to display multiple articles per request.
 * Displays BOM articles grouped by their root, with navigation to BOM detail view.
 */
export default function ArticlesExpandedRow({ requestArticles }: Props) {
  const { t } = useTranslation(['request', 'common'])
  const history = useHistory()
  const log = new LogTool({ context: 'ArticlesExpandedRow', enable: false, logLevel: 'debug' })

  // State for BOM processing
  const [bomArticleGroups, setBomArticleGroups] = useState<BOMArticleGroup[]>([])
  const [regularArticles, setRegularArticles] = useState<ArticleWithQuantity[]>([])
  const [isLoadingBOM, setIsLoadingBOM] = useState(false)
  const processedArticlesRef = useRef<string>('')

  // Create a stable key from article self URLs to detect when articles actually change
  const articlesKey = useMemo(() => {
    if (!requestArticles || requestArticles.length === 0) return ''
    return requestArticles
      .map(ra => ra.article?.self)
      .filter(Boolean)
      .sort()
      .join(',')
  }, [requestArticles])

  /**
   * Navigate to BOM detail page
   */
  const handleOpenBOMDetail = useCallback((bomRootArticle: Article) => {
    log.debug('Opening BOM detail for', bomRootArticle)
    history.push({
      pathname: '/items/bom/detail',
      state: {
        bomBase: {
          ...bomRootArticle,
          articlePk: bomRootArticle.key,
          bomBaseSelf: bomRootArticle.self,
        }
      },
    })
  }, [history])

  // Process articles: separate BOM root articles and fetch their leaf articles
  useEffect(() => {
    if (!requestArticles || requestArticles.length === 0) {
      setBomArticleGroups([])
      setRegularArticles([])
      processedArticlesRef.current = ''
      return
    }

    // Skip if we've already processed these exact articles
    if (processedArticlesRef.current === articlesKey) {
      return
    }

    const processArticles = async () => {
      setIsLoadingBOM(true)
      const newBomGroups: BOMArticleGroup[] = []
      const newRegularArticles: ArticleWithQuantity[] = []
      const bomBaseCandidates = requestArticles
        .filter(item => Boolean(item.article?.isBomBase && item.article?.self))
        .map(item => ({
          self: item.article.self,
          name: item.article.name,
          number: item.article.number,
        }))
      const bomBaseSelfsInRequest = new Set(bomBaseCandidates.map(item => item.self))
      const bomBaseIdentifierToSelfs = new Map<string, string[]>()
      for (const candidate of bomBaseCandidates) {
        const identifiers = [candidate.number, candidate.name].filter(Boolean) as string[]
        for (const identifier of identifiers) {
          const existing = bomBaseIdentifierToSelfs.get(identifier)
          if (existing) {
            existing.push(candidate.self)
          } else {
            bomBaseIdentifierToSelfs.set(identifier, [candidate.self])
          }
        }
      }
      const nestedBomBaseSelfs = new Set<string>()

      for (const requestArticle of requestArticles) {
        const article = requestArticle.article
        if (!article?.self) continue

        // Check if this is a BOM base article
        if (article.isBomBase) {
          if (nestedBomBaseSelfs.has(article.self)) {
            continue
          }
          try {
            // Fetch leaf articles for this BOM
            const leafArticles = await fetchBOMLeafArticles(article.self)
            for (const leaf of leafArticles) {
              if (!Array.isArray(leaf.parent_path)) continue
              for (const parentIdentifier of leaf.parent_path) {
                const matches = bomBaseIdentifierToSelfs.get(parentIdentifier)
                if (!matches) continue
                for (const matchSelf of matches) {
                  if (matchSelf !== article.self) {
                    nestedBomBaseSelfs.add(matchSelf)
                  }
                }
              }
            }

            const articlesWithQuantity: ArticleWithQuantity[] = leafArticles.map((leaf: BOMLeafArticle) => {
              const baseQuantity = leaf.bom_amount || 1
              return {
                article: {
                  self: leaf.self,
                  name: leaf.name,
                  number: leaf.number,
                  hasChildren: leaf.hasChildren,
                  isBomBase: leaf.isBomBase,
                  material: leaf.material,
                  key: leaf.self,
                  lastModifiedAt: new Date(),
                } as Article,
                quantity: baseQuantity * requestArticle.quantity,
                baseQuantity: baseQuantity,
                bomRootName: article.name || article.number,
                bomRootSelf: article.self,
                parentPath: leaf.parent_path,
              }
            })

            newBomGroups.push({
              bomRootName: article.name || article.number || '-',
              bomRootSelf: article.self,
              bomRootArticle: article,
              bomRootQuantity: requestArticle.quantity,
              articles: articlesWithQuantity,
            })
          } catch (error) {
            log.error('Error fetching BOM leaf articles for:', article.self, error)
            // If fetching fails, treat it as a regular article
            newRegularArticles.push({
              article,
              quantity: requestArticle.quantity,
            })
          }
        } else {
          // Regular article (non-BOM)
          newRegularArticles.push({
            article,
            quantity: requestArticle.quantity,
          })
        }
      }

      const filteredBomGroups = newBomGroups.filter(
        group => !nestedBomBaseSelfs.has(group.bomRootSelf)
      )

      const bomLeafSelfs = new Set(
        filteredBomGroups.flatMap(group => group.articles.map(item => item.article.self).filter(Boolean))
      )
      const filteredRegularArticles = newRegularArticles.filter(
        item => !item.article?.self || !bomLeafSelfs.has(item.article.self)
      )

      // Mark these articles as processed
      processedArticlesRef.current = articlesKey

      setBomArticleGroups(filteredBomGroups)
      setRegularArticles(filteredRegularArticles)
      setIsLoadingBOM(false)
    }

    void processArticles()
  }, [articlesKey, requestArticles])

  const requestArticleQuantities = useMemo(() => {
    if (!requestArticles) return new Map<string, number>()
    const entries = requestArticles
      .map(item => {
        const self = item.article?.self
        if (!self) return null
        return [self, item.quantity ?? 0] as const
      })
      .filter(Boolean) as Array<readonly [string, number]>
    return new Map(entries)
  }, [requestArticles])

  const getAdditionalParts = useCallback(
    (articleSelf: string | undefined, expectedQuantity: number): number => {
      if (!articleSelf) return 0
      const actualQuantity = requestArticleQuantities.get(articleSelf) ?? 0
      const additional = actualQuantity - expectedQuantity
      return additional > 0 ? additional : 0
    },
    [requestArticleQuantities]
  )

  if (!requestArticles || requestArticles.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('common:content.label.noData')}
        </Typography>
      </Box>
    )
  }

  // Show loading state while processing BOMs
  if (isLoadingBOM) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          {t('common:content.label.loading')}
        </Typography>
      </Box>
    )
  }

  // Define columns for BOM leaf articles table
  const bomLeafColumns: ColumnsType<ArticleWithQuantity> = [
    {
      title: t('common:content.label.name'),
      dataIndex: ['article', 'name'],
      key: 'name',
      render: (_, record) => (
        <Tooltip title={record.parentPath?.join(' > ') || ''}>
          <span>{record.article.name || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: t('common:content.label.articleNumber'),
      dataIndex: ['article', 'number'],
      key: 'number',
      render: (_, record) => record.article.number || '-',
    },
    {
      title: t('request:content.label.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (quantity: number) => quantity ?? 1,
    },
    {
      title: t('request:content.label.additionalParts', { defaultValue: 'Additional parts' }),
      key: 'additionalParts',
      width: 140,
      render: (_, record) => {
        const expectedQuantity = record.quantity ?? 0
        const additional = getAdditionalParts(record.article?.self, expectedQuantity)
        return additional > 0 ? additional : 0
      },
    },
  ]

  // Define columns for regular articles table
  const regularColumns: ColumnsType<ArticleWithQuantity> = [
    {
      title: t('common:content.label.name'),
      dataIndex: ['article', 'name'],
      key: 'name',
      render: (_, record) => record.article?.name ?? '-',
    },
    {
      title: t('common:content.label.articleNumber'),
      dataIndex: ['article', 'number'],
      key: 'number',
      render: (_, record) => record.article?.number ?? '-',
    },
    {
      title: t('request:content.label.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (quantity: number) => quantity ?? 1,
    },
    {
      title: t('request:content.label.additionalParts', { defaultValue: 'Additional parts' }),
      key: 'additionalParts',
      width: 140,
      render: (_, record) => {
        const expectedQuantity = record.quantity ?? 0
        const additional = getAdditionalParts(record.article?.self, expectedQuantity)
        return additional > 0 ? additional : 0
      },
    },
  ]

  return (
    <Box sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)', width: '100%' }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        {t('request:content.label.articlesInRequest')}
      </Typography>

      {/* BOM Article Groups */}
      {bomArticleGroups.map((group, groupIndex) => (
        <Box key={group.bomRootSelf || groupIndex} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, color: 'primary.main', fontWeight: 'medium' }}>
            {group.bomRootName} ({t('request:content.label.bomArticles')})
          </Typography>

          {/* BOM Root Article Row */}
          <Box sx={{
            mb: 2,
            p: 1.5,
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  {t('request:content.label.bomRootArticle')}:
                </Typography>
                <Typography variant="body2">
                  {group.bomRootArticle.name || group.bomRootArticle.number || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({group.bomRootArticle.number})
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">
                  {t('request:content.label.bomQuantity')}: {group.bomRootQuantity}
                </Typography>

                <Tooltip title={t('request:interaction.button.openBomDetail')}>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => handleOpenBOMDetail(group.bomRootArticle)}
                  >
                    <AccountTree />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* BOM Leaf Articles Table */}
          {group.articles.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {t('request:content.label.bomLeafArticles')} ({group.articles.length})
              </Typography>
              <Table
                columns={bomLeafColumns}
                dataSource={group.articles.map((item, index) => ({
                  ...item,
                  key: item.article.self || index,
                }))}
                pagination={false}
                size="small"
                bordered
              />
            </>
          )}
        </Box>
      ))}

      {/* Regular Articles Table */}
      {regularArticles.length > 0 && (
        <Box sx={{ mt: bomArticleGroups.length > 0 ? 2 : 0 }}>
          {bomArticleGroups.length > 0 && (
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {t('request:content.label.articles')} ({regularArticles.length})
            </Typography>
          )}
          <Table
            columns={regularColumns}
            dataSource={regularArticles.map((item, index) => ({
              ...item,
              key: item.article.self || index,
            }))}
            pagination={false}
            size="small"
            bordered
          />
        </Box>
      )}
    </Box>
  )
}
